import PropTypes from 'prop-types';
import { useState, useCallback } from 'react';
// @mui
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import CardHeader from '@mui/material/CardHeader';
import ButtonBase from '@mui/material/ButtonBase';
import Card from '@mui/material/Card';
import {BarChart} from '@mui/x-charts/BarChart';
// components
import Iconify from '../../../components/iconify';
import Chart, { useChart } from '../../../components/chart';
import CustomPopover, { usePopover } from '../../../components/custom-popover';
import { fCurrencyEuro, fCurrencyEuroWith00 } from '../../../utils/format-number';

// ----------------------------------------------------------------------


export default function ChartPiledBars({ title, subheader, chart, color, ...other }) {
  const theme = useTheme();

  const {
    categories,
    series,
    options,
  } = chart;

  const seriesFilter = [
    { label: "12 meses", minIndex: 0},
    { label: "6 meses", minIndex: 7 },
    { label: "3 meses", minIndex: 10 },
  ];
  
  const popover = usePopover();

  const [seriesData, setSeriesData] = useState('12 meses');

  const filteredCategories = categories.filter((_, idx) => idx >= seriesFilter.find(option => option.label === seriesData).minIndex);

  const chartOptions = useChart({
    colors: color.constructor === Array ? color : [color],
    xaxis: {
      categories: filteredCategories,
    },
    yaxis: {
      labels: {
        formatter: (value) => fCurrencyEuro(value || '0'),
      }
    },
    ...options,
  });

  const chartSetting = {
    yAxis: [
      {
        valueFormatter: (value) => fCurrencyEuro(value || '0'),
      },
    ],
  };

  const handleChangeSeries = useCallback(
    (newValue) => {
      popover.onClose();
      setSeriesData(newValue);
    },
    [popover]
  );

  const filteredSeries = series.map(entry => ({
    ...entry,
    data: entry.data.filter((_, idx) => idx >= seriesFilter.find(option => option.label === seriesData).minIndex)
  }));

  return (
    <>
      <Card {...other}>
        <CardHeader
          title={title}
          subheader={subheader}
          action={
            <ButtonBase
              onClick={popover.onOpen}
              sx={{
                pl: 1,
                py: 0.5,
                pr: 0.5,
                borderRadius: 1,
                typography: 'subtitle2',
                bgcolor: 'background.neutral',
              }}
            >
              {seriesData}

              <Iconify
                width={16}
                icon={popover.open ? 'eva:arrow-ios-upward-fill' : 'eva:arrow-ios-downward-fill'}
                sx={{ ml: 0.5 }}
              />
            </ButtonBase>
          }
        />

        <Box sx={{ mt: 3, mx: 3 }}>
          {/* <Chart
            dir="ltr"
            type="bar"
            series={filteredSeries}
            options={chartOptions}
            height={364}
          /> */}
          <BarChart
              height={400}
              grid={{horizontal: true}}
              margin={{top: 100, left: 70}}
              series={filteredSeries.map(serie => ({
                data: serie.data,
                label: serie.name,
                stack: 'total',
                valueFormatter: (value) => fCurrencyEuroWith00(Number(value) || '0'),
              }))}
              xAxis={[
                {
                  data: filteredCategories,
                  scaleType: 'band',
                  tickLabelStyle: {
                    angle: -45,
                    textAnchor: 'end',
                    fontSize: 12,
                  },
                },
              ]}
              colors={theme.palette.simplai.array}
              {...chartSetting}
            />
        </Box>
      </Card>

      <CustomPopover open={popover.open} onClose={popover.onClose} sx={{ width: 140 }}>
        {seriesFilter.map((option) => (
          <MenuItem
            key={option.label}
            selected={option.label === seriesData}
            onClick={() => handleChangeSeries(option.label)}
          >
            {option.label}
          </MenuItem>
        ))}
      </CustomPopover>
    </>
  );
}

ChartPiledBars.propTypes = {
  chart: PropTypes.object,
  subheader: PropTypes.string,
  title: PropTypes.string,
  color: PropTypes.string,
};
